import { useInternalState, assignMswData, serializeQuery } from '../hooks/internalState';
import {
  PutMeFollowingResult,
  PutMeFollowingOptions,
  DeleteMeFollowingResult,
  DeleteMeFollowingOptions,
  GetMeFollowingOptions,
  GetMeFollowingResult,
  getMeFollowing,
  PutMeFollowingFields,
  DeleteMeFollowingFields,
  GetMeFollowingFields,
  putMeFollowing,
  deleteMeFollowing,
} from '../fetchers/me/following';
import { useCallback } from 'react';
import useSWR, { SWRConfiguration, useSWRConfig } from 'swr';
import { useGctlConfig } from '../hooks/GctlProvider';

/**
 * DO NOT MODIFY
 * Auto-generated by gctl-plugin-vimeo-hooks
 */

type UseGetMeFollowingOptions<Fields extends GetMeFollowingFields> = GetMeFollowingOptions<Fields>;

/**
 * Check if the user is following another user
 * This method determines whether the authenticated user is a follower of the specified user.
 */

export function useGetMeFollowing<Fields extends GetMeFollowingFields>(
  getOptions: UseGetMeFollowingOptions<Fields> | (() => UseGetMeFollowingOptions<Fields> | null),
  config?: SWRConfiguration<GetMeFollowingResult<Fields>>,
) {
  const options = typeof getOptions === 'function' ? getOptions() : getOptions;

  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  return useSWR(
    options ? `/me/following/${options.where.followUserId}${serializeQuery(options)}` : () => null,
    options
      ? () => {
          return getMeFollowing({
            ...options,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
            baseUrl,
          });
        }
      : null,
    config,
  );
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetMeFollowing, {
    endpoint: '/me/following/:followUserId',
    method: 'GET',
  });
}

/**
 * Check if the user is following another user
 * This method determines whether the authenticated user is a follower of the specified user.
 */

export function useGetMeFollowingLazy<Fields extends GetMeFollowingFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<GetMeFollowingResult<Fields> | undefined>();

  let callback = useCallback(
    async (options: UseGetMeFollowingOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/me/following/${options.where.followUserId}${serializeQuery(options)}`,
          getMeFollowing({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [callback, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetMeFollowingLazy, {
    endpoint: '/me/following/:followUserId',
    method: 'GET',
  });
}

type UsePutMeFollowingOptions<Fields extends PutMeFollowingFields> = PutMeFollowingOptions<Fields>;

/**
 * Follow a specific user
 * This method causes the authenticated user to become the follower of the specified user.
 */

export function usePutMeFollowing<Fields extends PutMeFollowingFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<PutMeFollowingResult<Fields> | undefined>();

  let call = useCallback(
    async (options: UsePutMeFollowingOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/me/following/${options.where.followUserId}${serializeQuery(options)}`,
          putMeFollowing({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
          false,
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(usePutMeFollowing, {
    endpoint: '/me/following/:followUserId',
    method: 'PUT',
  });
}

type UseDeleteMeFollowingOptions<Fields extends DeleteMeFollowingFields> =
  DeleteMeFollowingOptions<Fields>;

/**
 * Unfollow a user
 * This method causes the authenticated user to stop following another user.
 */

export function useDeleteMeFollowing<Fields extends DeleteMeFollowingFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<DeleteMeFollowingResult<Fields> | undefined>();

  let call = useCallback(
    async (options: UseDeleteMeFollowingOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/me/following/${options.where.followUserId}${serializeQuery(options)}`,
          deleteMeFollowing({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
          false,
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useDeleteMeFollowing, {
    endpoint: '/me/following/:followUserId',
    method: 'DELETE',
  });
}
